import { api } from './client'

/**
 * F-052 — report del call center (`CallCenterReportController`). Nessun importo in questi payload.
 * `from`/`to` sono giorni "YYYY-MM-DD" (estremi inclusi); se omessi il backend usa gli ultimi 30 giorni.
 */
export interface CallCenterOperatorRow {
  operatorUserId: number
  calls: number
  usefulContacts: number
  distinctLeadsWorked: number
  /** Lead il cui PRIMO tentativo in assoluto è di questo operatore (denominatore del tempo medio). */
  firstAttemptLeads: number
  averageMinutesToFirstAttempt: number | null
  appointmentsSet: number
  appointmentsCompleted: number
  appointmentsNoShow: number
  contractsGenerated: number
  activations: number
  /** Closing Rate (contratti / appuntamenti effettuati), 0..1; null se nessun appuntamento effettuato. */
  conversionRate: number | null
  /** F-115: true/false con il registro di presenza acceso, null altrimenti. */
  online?: boolean | null
}

export interface CallCenterOperatorsReport {
  from: string
  to: string
  scope: 'all' | 'own'
  conversionRateFormula: string
  operators: CallCenterOperatorRow[]
  /** Dati non rappresentabili oggi, dichiarati invece di mostrare zeri. */
  gaps: string[]
}

export interface SpeedToLeadRow {
  source: string
  label: string
  targetMinutes: number | null
  total: number
  withinSla: number
  outsideSla: number
  pending: number
  averageMinutes: number | null
  medianMinutes: number | null
}

export interface SpeedToLeadReport {
  from: string
  to: string
  targetsProvisional: boolean
  leadsWithUnrecognisedSource: number
  sources: SpeedToLeadRow[]
  total: SpeedToLeadRow
}

export interface CallCenterReportFilters {
  from?: string | undefined
  to?: string | undefined
}

function qs(f: CallCenterReportFilters): string {
  const p = new URLSearchParams()
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** F-084 (REQ-112-001): cruscotto del responsabile. Nessun importo. */
export interface CallCenterManagerDashboard {
  asOf: string
  period: { from: string; toExclusive: string; days: number; timeZone: string }
  provisional: { activeMinutes: number; activeOperatorsIsProxyOfOnline: boolean; speedToLeadTargets: boolean }
  newLeads: number
  unassignedLeads: number
  outOfSlaLeads: number
  newLeadsWithoutSlaTarget: number
  notWorkedLeads: number
  overdueCallbacks: number
  /** Operatori con un tentativo negli ultimi `activeMinutes` minuti: proxy dichiarato di "online". */
  activeOperators: number
  /** F-115 (REQ-112-001): false con la presenza spenta; gli altri campi sono allora null. */
  presenceEnabled?: boolean
  onlineWindowMinutes?: number | null
  /** Operatori (ruolo EqualOperatore o abbinati a una campagna) attivi e online entro la finestra. */
  onlineOperators?: number | null
  onlineOperatorIds?: number[] | null
  onlineOperatorList?: { userId: number; name: string; lastSeenAt: string; minutesAgo: number }[] | null
  leadsWorked: number
  appointmentsSet: number
  appointmentRate: number | null
  appointmentsNoShow: number
  noShowRate: number | null
  appointmentsCompleted: number
  completedOnSet: number | null
  leadsConvertedOnWorked: number
  convertedOnWorked: number | null
  definitions: string[]
}

export interface CallCenterInboxItem {
  id: number
  name: string
  phone: string | null
  source: string | null
  sourceLabel: string | null
  campaign: string | null
  sector: string | null
  temperature: string | null
  score: number | null
  createdAt: string
  waitingMinutes: number
  slaTargetMinutes: number | null
  outOfSla: boolean
}

export interface CallCenterInbox {
  total: number
  page: number
  pageSize: number
  items: CallCenterInboxItem[]
  slaTargetsProvisional: boolean
}

export interface LeadOperationalStateRow {
  leadId: number
  state: { code: string; label: string; finalOutcome: string | null; lostReason: string | null }
}

export const callCenterReportApi = {
  manager: (days = 1, activeMinutes = 15) =>
    api<CallCenterManagerDashboard>(`/api/reports/call-center/manager?days=${days}&activeMinutes=${activeMinutes}`),
  inbox: (page = 1, pageSize = 25) =>
    api<CallCenterInbox>(`/api/reports/call-center/inbox?page=${page}&pageSize=${pageSize}`),
  /** Al massimo 200 id; i lead non visibili al chiamante non compaiono nella risposta. */
  operationalStates: (ids: number[]) =>
    ids.length === 0
      ? Promise.resolve({ items: [] as LeadOperationalStateRow[] })
      : api<{ items: LeadOperationalStateRow[] }>(`/api/reports/call-center/operational-states?ids=${ids.join(',')}`),
  operators: (f: CallCenterReportFilters = {}) =>
    api<CallCenterOperatorsReport>(`/api/reports/call-center/operators${qs(f)}`),
  speedToLead: (f: CallCenterReportFilters = {}) =>
    api<SpeedToLeadReport>(`/api/reports/call-center/speed-to-lead${qs(f)}`),
}
